/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import EventEmitter from 'eventemitter3';

import { isNonEmptyArray } from '../../util.type';
import { DateNowFactory } from '../date/date-util';
import ErrorLevel from '../error/error-level';
import ErrorLevelUtil from '../error/error-level-util';
import { errorWithLevelWarning } from '../error/error-with-level';
import { toISOString } from '../iso-string';
import PromiseUtil from '../promise/promise-util';
import { errorWithAnalyticsStackTrace } from './analytics-transporter';

// TODO #555: Není ideální mít import testovacího kódu u produkčního, ale u typů to snad nevadí.
/**
 * @import { GlobalTimers } from '@sinonjs/fake-timers'
 * @import { IAnalyticsTransporter,  MutexContext } from '.'
 * @import { IDateUTC, NowFactory } from '../date'
 * @import { ErrorEventListener } from '../event-listeners'
 * @import { AnalyticsCounter } from './analytics-counter'
 * @import AnalyticsStore from './analytics-store'
 */

/**
 * @typedef {import('../event-bus').EventBus<EventTypes>} EventBus
 */

/**
 * @typedef EventTypes
 * @property {ErrorEventListener} error
 */

/**
 * Maximální doba prvního náhodného čekání a opakovaná perioda. [ms]
 */
export const periodInMs = 60 * 60 * 1000;

/**
 * @implements {EventBus}
 */
export default class AnalyticsSender {
  /**
   * @param {AnalyticsStore} analyticsStore
   * @param {IAnalyticsTransporter} analyticsTransporter
   * @param {Object} [opts = {}]
   * @param {NowFactory} [opts.nowFactory] Továrna na aktuální čas.
   * @param {GlobalTimers['setTimeout']} [opts.setTimeout]
   */
  constructor(
    analyticsStore,
    analyticsTransporter,
    {
      nowFactory = new DateNowFactory(),
      // Pozor, extrémně nebezpečné bez .bind(window): TypeError: Illegal invocation
      setTimeout = window.setTimeout.bind(window)
    } = {}
  ) {
    /** @private */
    this.analyticsStore = analyticsStore;
    /** @private */
    this.analyticsTransporter = analyticsTransporter;

    /** @private */
    this.nowFactory = nowFactory;
    /** @private */
    this.setTimeout = setTimeout;

    /** @private @type {EventEmitter<EventTypes>} */
    this.emitter = new EventEmitter();
    // Mělo by být `@private`, ale nemůže kvůli testům.
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    this.timeout = undefined;
  }

  /** @type {EventBus['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {EventBus['on']} */
  on(...args) {
    this.emitter.on(...args);
  }

  // Mělo by být `@private`, ale nemůže kvůli testům.
  /**
   * Reakce na chybu.
   * @param {Error} error Chybový objekt.
   */
  onError(error) {
    this.emitter.emit('error', error);
  }

  // Mělo by být `@private`, ale nemůže kvůli testům.
  /**
   * Periodicky volaná metoda, která se stará o odesílání analytických dat.
   * @returns {Promise<void>} Vyřeší se až po odeslání všech vhodných analytických dat.
   */
  async onTimeout() {
    try {
      await this.analyticsStore.doInMutex((ctx) =>
        this.send(ctx, this.nowFactory.now())
      );
    } catch (error) {
      // Metoda send by neměla vrhnout, protože sama emituje errory.
      // Tím pádem toto musí být nějaká neočekávaná (programátorská) chyba.
      this.onError(/** @type {Error} */ (error));
    }

    // Nastavení dalšího spuštění na další periodu.
    this.timeout = this.setTimeout(this.onTimeout.bind(this), periodInMs);
  }

  /**
   * Odešle uzavřená analytická data vzhledem k {@link now}.
   *
   * Tato metoda "nemůže nikdy" vrhnout (to by byla programátorská chyba),
   * jelikož rovnou emituje aggregované warningy a errory (max. 1 AggregateError
   * pro warningy a max. 1 AggregateError pro errory per volání).
   * @param {MutexContext} ctx
   * @param {IDateUTC} now
   */
  async send(ctx, now) {
    const { deviceId, serverUrl } = ctx.serverDeviceId;
    const groups = await ctx.store.findAllAnalyticsGroup({
      // Filtr na `serverUrl` a `deviceId` pre robustnosť, nemalo by nastať,
      // že by existovali analytické dáta s neaktuálnymi ServerDeviceId,
      // keďže ich pred spuštěním smazal migrátor.
      where: { closed: { '<=': toISOString(now) }, deviceId, serverUrl },
      orderBy: 'opened'
    });

    const warnings = /** @type {Error[]} */ ([]);
    const errors = /** @type {Error[]} */ ([]);
    await PromiseUtil.allSerial(
      groups.map((group) => async () => {
        const analyticsGroupId = group.id;
        /** @type {AnalyticsCounter[] | undefined} */
        let counters;
        try {
          // TODO #389: Kvůli testovatelnosti pomocí Sinon spy/stub a magii JSData
          // musíme bohužel udělat kopii pole, jinak by kód sice fungoval dobře,
          // ale v testech by se vracelo např. falešně prázdné pole.
          counters = [
            ...(await ctx.store.findAllAnalyticsCounter({
              where: { analyticsGroupId }
            }))
          ];
          if (isNonEmptyArray(counters)) {
            await this.analyticsTransporter
              .transport(group, counters)
              .catch((err) => {
                const error = /** @type {Error} */ (err);
                // Pokud se nejedná o "trvalou" chybu, tak další kolo odesílání
                // může uspět (a doufejme že eventuelně zaručeně uspěje, jinak
                // máme zombie data, která nikdy nikdo nesmaže a opakovaně se
                // budou činit pokusy o odeslání).
                if (error.name !== 'PermanentError') {
                  throw errorWithLevelWarning(error);
                }
                // Pokud je to "trvalá" chyba, tak chceme, aby se pokračovalo
                // a data se smazala. Proto ale musíme pushnout chybu s custom
                // stackem do errorů, aby přišla na Sentry, a zde nevyhazovat.
                errors.push(
                  errorWithAnalyticsStackTrace(error, group, counters)
                );
              });
          }

          // Smazání analytických objektů ve vhodném pořadí pro případ selhání.
          await ctx.store.destroyAnalyticsGroup(analyticsGroupId);
          await ctx.store.destroyAllAnalyticsCounter({
            where: { analyticsGroupId }
          });
        } catch (err) {
          const error = /** @type {Error & { level?: ErrorLevel; }} */ (err);
          // TODO #389: Nebo porovnávat obecně úrovňově pomocí ErrorLevelUtil.compare,
          // kdyby to nebylo Error/Warning, ale i třeba Log/Info/Debug?
          if (ErrorLevelUtil.fromValue(error) === ErrorLevel.Warning) {
            // V případě warningu chceme prefixovat message, smazat level
            // (bude u AggregateError) a jelikož se to to bude prakticky odesílat
            // do ConsoleLogger místo na Sentry, tak chceme ponechat stack.
            let message = `${analyticsGroupId}`;
            if (error.message) {
              message += `: ${error.message}`;
            }
            error.message = message;
            delete error.level;
            warnings.push(error);
          } else {
            errors.push(errorWithAnalyticsStackTrace(error, group, counters));
          }
        }
      })
    );

    if (warnings.length > 0) {
      this.onError(
        errorWithLevelWarning(
          new AggregateError(
            warnings,
            'AnalyticsSender: transport warnings (maybe retriable)'
          )
        )
      );
    }
    if (errors.length > 0) {
      this.onError(
        new AggregateError(
          errors,
          'AnalyticsSender: destroy errors (possible data duplication when re-sending) or permanent transport errors'
        )
      );
    }
  }

  /**
   * Zaháji odosielanie zozbieraných analytických dát se zpožděním v rozmezí
   * 0 až periodInMs pro rovnoměrné rozložení zátěže.
   */
  start() {
    // Pro idempotenci.
    this.stop();

    this.timeout = this.setTimeout(
      this.onTimeout.bind(this),
      Math.floor(Math.random() * periodInMs)
    );
  }

  /**
   * Zastaví odosielanie zozbieraných analytických dát.
   */
  stop() {
    clearTimeout(this.timeout);
    this.timeout = undefined;
  }
}
